import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import CommonPageLayout from "../../components/commonPageLayout/commonPageLayout";
import CustomDataTable from "../../components/customTable/CustomDataTable";
import CustomButton from "../../components/CommonButton/CommonButton";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  InputAdornment,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AddIcon from "@mui/icons-material/Add";
import {
  CREATE_PROGRAM_PATH,
  GET_PROGRAMS_PATH,
  UPDATE_PROGRAM_PATH
} from "../../constants/apipaths";
import { getFormattedDate } from "../../Utils/commonFunction";
import RefreshButton from "../../components/RefreshButton/RefreshButton";
import Loader from "../../components/loader/loader";
import { useAppSelector } from "../../redux/hooks";

interface ProgramItem {
  Id: number;
  PointActionId: number;
  Points: number;
  Item: string;
  IsActive?: number;
  Created?: string;
  Updated?: string;
  MerchantId?: number;
}

interface ProgramFormValues {
  Id?: number;
  PointActionId: number;
  Points: string;
  Item: string;
  Item2?: string;
  Item3?: string;
  IsActive?: number;
}

const getDefaultFormValues = (): ProgramFormValues => ({
  PointActionId: 2,
  Points: "",
  Item: "",
  Item2: "",
  Item3: "",
  IsActive: 1
});

const ManagePrograms: React.FC = () => {
  let currentBusiness: any = localStorage?.getItem("currentBusiness");
  const state: any = {
    token: localStorage?.getItem("businessAccessToken"),
    currentBusiness: currentBusiness ? JSON.parse(currentBusiness) : null
  };

  const businessId: string | undefined =
    state?.currentBusiness?.BusinessId || state?.currentBusiness?.businessId;

  const pointActionOptions = useMemo(
    () => [
      { value: 2, label: "Count", itemType: "textbox" },
      { value: 3, label: "Visit", itemType: "fixed" },
      { value: 4, label: "Dollars", itemType: "textbox" },
      { value: 5, label: "Multi Count", itemType: "textbox" }
    ],
    []
  );

  const [programs, setPrograms] = useState<ProgramItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState({
    message: "",
    type: "success" as "success" | "error" | "info" | "warning"
  });

  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formValues, setFormValues] = useState<ProgramFormValues>(() =>
    getDefaultFormValues()
  );
  const [showThirdItem, setShowThirdItem] = useState<boolean>(false);
  const [touchedFields, setTouchedFields] = useState({
    Points: false,
    Item: false,
    Item2: false,
    Item3: false
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [isErrorOnlyMode, setIsErrorOnlyMode] = useState(false);

  // Confirmation modal states for multi-count activation/deactivation
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    program: ProgramItem | null;
    relatedPrograms: ProgramItem[];
    availablePrograms?: ProgramItem[]; // For multi-select activation
    message: string;
    action?: "activate" | "deactivate";
  }>({
    open: false,
    program: null,
    relatedPrograms: [],
    availablePrograms: [],
    message: "",
    action: "deactivate"
  });
  const [isConfirmModalLoading, setIsConfirmModalLoading] =
    useState<boolean>(false);
  const [selectedProgramsToActivate, setSelectedProgramsToActivate] = useState<
    number[]
  >([]);
  const [confirmModalError, setConfirmModalError] = useState<string | null>(
    null
  );

  // Global billing / restriction state from Redux
  const { isNonPaying, isBillingLoading } = useAppSelector(
    (s) => s.businessAccess
  );

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${state.token}`,
      "Content-Type": "application/json"
    }),
    [state.token]
  );

  const activeProgramsCount = useMemo(
    () => programs.filter((p) => p.IsActive === 1).length,
    [programs]
  );

  const availableActiveSlots = Math.max(0, 3 - activeProgramsCount);

  const fetchPrograms = useCallback(async () => {
    if (!businessId) return;
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}${GET_PROGRAMS_PATH}/${businessId}`,
        { headers }
      );
      const list: ProgramItem[] = Array.isArray(response?.data)
        ? response.data
        : response?.data?.Programs || response?.data?.LoyaltyProgram || [];
      setPrograms(list || []);
    } catch (error) {
      console.error("Error fetching programs:", error);
    } finally {
      setIsLoading(false);
    }
  }, [businessId, headers]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  const resetFormState = useCallback(() => {
    setFormValues(getDefaultFormValues());
    setShowThirdItem(false);
    setNotificationMessage({ message: "", type: "success" });
    setTouchedFields({
      Points: false,
      Item: false,
      Item2: false,
      Item3: false
    });
    setHasSubmitted(false);
    setModalError(null);
    setIsErrorOnlyMode(false);
  }, []);

  // Find related multi-count programs (same PointActionId = 5 and same Points)
  // Related programs are those created together as part of the same Multi Count group
  const findRelatedMultiCountPrograms = useCallback(
    (program: ProgramItem): ProgramItem[] => {
      if (program.PointActionId !== 5) return [];

      // Convert Points to numbers for comparison to handle type inconsistencies
      const programPoints = Number(program.Points);
      
      // First, try to find programs with same Points value (primary grouping method)
      let related = programs.filter(
        (p) =>
          p.Id !== program.Id &&
          p.PointActionId === 5 &&
          Number(p.Points) === programPoints
      );

      // If no programs found with same Points, try grouping by creation time
      // (Multi Count programs created together should have similar timestamps)
      if (related.length === 0 && program.Created) {
        const programCreatedTime = new Date(program.Created).getTime();
        const timeWindow = 5 * 60 * 1000; // 5 minutes window
        
        related = programs.filter(
          (p) =>
            p.Id !== program.Id &&
            p.PointActionId === 5 &&
            p.Created &&
            Math.abs(new Date(p.Created).getTime() - programCreatedTime) <= timeWindow
        );
      }

      // Debug logging
      console.log("findRelatedMultiCountPrograms - Debug:", {
        currentProgram: {
          Id: program.Id,
          PointActionId: program.PointActionId,
          Points: program.Points,
          PointsType: typeof program.Points,
          Created: program.Created
        },
        allPrograms: programs.map(p => ({
          Id: p.Id,
          PointActionId: p.PointActionId,
          Points: p.Points,
          PointsType: typeof p.Points,
          IsActive: p.IsActive,
          Created: p.Created
        })),
        multiCountPrograms: programs.filter(p => p.PointActionId === 5).map(p => ({
          Id: p.Id,
          Points: p.Points,
          PointsType: typeof p.Points,
          Created: p.Created
        })),
        relatedCount: related.length,
        relatedPrograms: related
      });

      return related;
    },
    [programs]
  );

  const shouldShowError = (fieldName: keyof typeof touchedFields) =>
    touchedFields[fieldName] || hasSubmitted;

  const isFormValid = useCallback(() => {
    if (!formValues.PointActionId) {
      return false;
    }

    const pointsValue = Number(formValues.Points);
    if (isNaN(pointsValue) || pointsValue <= 0) {
      return false;
    }

    const selectedAction = pointActionOptions.find(
      (opt) => opt.value === formValues.PointActionId
    );
    const isMultiCount = formValues.PointActionId === 5;
    const isDollars = formValues.PointActionId === 4;

    if (selectedAction?.itemType === "fixed") {
      return !!(formValues.Item && formValues.Item.trim());
    }

    if (isMultiCount) {
      if (formMode === "edit") {
        return !!(formValues.Item && formValues.Item.trim());
      }
      if (!formValues.Item || formValues.Item.trim() === "") {
        return false;
      }
      if (!formValues.Item2 || formValues.Item2.trim() === "") {
        return false;
      }
      return true;
    }

    if (!formValues.Item || formValues.Item.trim() === "") {
      return false;
    }

    if (isDollars) {
      const dollarAmount = parseFloat(formValues.Item);
      if (
        isNaN(dollarAmount) ||
        dollarAmount <= 0 ||
        !Number.isInteger(dollarAmount)
      ) {
        return false;
      }
    }

    return true;
  }, [formMode, formValues, pointActionOptions]);

  const handleOpenCreate = () => {
    if (isNonPaying) {
      setModalError(
        "Access limited: Please renew your contract or fix payment issues to add programs."
      );
      setIsErrorOnlyMode(true);
      setOpenModal(true);
      return;
    }
    resetFormState();
    setFormMode("create");
    if (activeProgramsCount >= 3) {
      setModalError(
        "Max, three programs can be active at a time. Please de-activate another program to create new one"
      );
      setIsErrorOnlyMode(true);
    }
    setOpenModal(true);
  };

  const handleOpenEdit = (item: ProgramItem) => {
    if (isNonPaying) {
      setModalError(
        "Access limited: Please renew your contract or fix payment issues to edit programs."
      );
      setIsErrorOnlyMode(true);
      setOpenModal(true);
      return;
    }
    resetFormState();
    setFormMode("edit");
    setFormValues({
      Id: item.Id,
      PointActionId: item.PointActionId ?? 2,
      Points: String(item.Points ?? ""),
      Item: item.Item || "",
      Item2: "",
      Item3: "",
      IsActive: item.IsActive ?? 1
    });
    setShowThirdItem(false);
    setIsErrorOnlyMode(false);
    setModalError(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setFormMode("create");
    resetFormState();
    setIsModalLoading(false);
    setOpenModal(false);
  };

  const handleSave = async () => {
    if (!businessId || isErrorOnlyMode) return;

    if (isNonPaying) {
      setModalError(
        "Access limited: Please renew your contract or fix payment issues to save program changes."
      );
      return;
    }

    setHasSubmitted(true);
    setNotificationMessage({ message: "", type: "success" });
    setModalError(null);

    if (!isFormValid()) {
      return;
    }

    const isMultiCount = formValues.PointActionId === 5;

    if (formMode === "create") {
      if (!isMultiCount && availableActiveSlots <= 0) {
        setModalError(
          "Max, three programs can be active at a time. Please de-activate another program to create new one"
        );
        return;
      }

      if (isMultiCount) {
        const optionalThirdFilled =
          !!formValues.Item3 && formValues.Item3.trim() !== "";
        const programsToCreate = optionalThirdFilled ? 3 : 2;
        if (programsToCreate > availableActiveSlots) {
          setModalError(
            "Cannot create Multi Count program. Maximum 3 active programs allowed. Reduce the number of items or deactivate another program."
          );
          return;
        }
      }
    }

    setIsModalLoading(true);
    try {
      if (formMode === "create") {
        if (isMultiCount) {
          // Multi Count: Call API for each item (2 required + 1 optional if filled)
          const payloads = [
            {
              PointActionId: formValues.PointActionId,
              Points: Number(formValues.Points),
              Item: formValues.Item
            },
            {
              PointActionId: formValues.PointActionId,
              Points: Number(formValues.Points),
              Item: formValues.Item2
            }
          ];

          // Add third item if it's filled
          if (formValues.Item3 && formValues.Item3.trim() !== "") {
            payloads.push({
              PointActionId: formValues.PointActionId,
              Points: Number(formValues.Points),
              Item: formValues.Item3
            });
          }

          await Promise.all(
            payloads.map((payload) =>
              axios.post(
                `${process.env.REACT_APP_API_BASE_URL}${CREATE_PROGRAM_PATH}/${businessId}`,
                payload,
                { headers }
              )
            )
          );

          const itemCount = payloads.length;
          setNotificationMessage({
            message: `Multi Count program with ${itemCount} item(s) created successfully`,
            type: "success"
          });
        } else {
          // Regular program creation
          const payload = {
            PointActionId: formValues.PointActionId,
            Points: Number(formValues.Points),
            Item: formValues.Item
          };
          await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}${CREATE_PROGRAM_PATH}/${businessId}`,
            payload,
            { headers }
          );
          setNotificationMessage({
            message: "Program created",
            type: "success"
          });
        }
      } else {
        // Edit mode - Multi Count programs should not be editable in the same way
        // If editing a Multi Count, treat it as a single item update
        const payload = {
          Id: formValues.Id,
          PointActionId: formValues.PointActionId,
          Points: Number(formValues.Points),
          Item: formValues.Item,
          IsActive: formValues.IsActive ?? 1
        };
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}${UPDATE_PROGRAM_PATH}/${businessId}`,
          payload,
          { headers }
        );
        setNotificationMessage({ message: "Program updated", type: "success" });
      }
      await fetchPrograms();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving program:", error);
      setModalError("Failed to save program");
    } finally {
      setIsModalLoading(false);
    }
  };

  const columns: any = [
    {
      field: "Item",
      headerName: "Item",
      width: 220,
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "PointActionId",
      headerName: "Point Action",
      width: 140,
      valueGetter: (p: any) => {
        const action = pointActionOptions.find(
          (opt) => opt.value === p.row.PointActionId
        );
        return action?.label || p.row.PointActionId;
      },
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "Points",
      headerName: "Points",
      width: 120,
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "IsActive",
      headerName: "Status",
      width: 120,
      renderCell: (params: any) => {
        const isActive = params.row.IsActive === 1;
        return (
          <Chip
            label={isActive ? "Active" : "Inactive"}
            color={isActive ? "success" : "default"}
            size="small"
            sx={{
              backgroundColor: isActive ? "#4caf50" : "#9e9e9e",
              color: "white",
              fontWeight: 500
            }}
          />
        );
      },
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "Created",
      headerName: "Creation Date",
      width: 200,
      valueGetter: (p: any) =>
        p.row.Created ? getFormattedDate(p.row.Created) : "-",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      sortable: false,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <CustomButton
            size="small"
            variant="outlined"
            onClick={() => handleOpenEdit(params.row)}
            disabled={isNonPaying || isBillingLoading}
          >
            Edit
          </CustomButton>
        </Box>
      ),
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell"
    }
  ];

  return (
    <CommonPageLayout
      heading="Programs"
      subHeading="Manage your Programs"
      docs={null}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <Box>
          {isNonPaying && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error">
                Access limited: Please renew your contract or fix payment issues
                to manage programs.
              </Alert>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <CustomButton
              variant="contained"
              className="blue_button"
              onClick={handleOpenCreate}
              disabled={isNonPaying || isBillingLoading}
            >
              Add Program
            </CustomButton>
            <RefreshButton onClick={fetchPrograms} loading={isLoading} />
          </Box>
          <CustomDataTable
            rows={programs}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } }
            }}
            pageSizeOptions={[10, 50, 100]}
            getRowId={(row: any) =>
              row?.Id ??
              `${row?.Item}-${row?.Points}-${row?.PointActionId}-${row?.Created}`
            }
            rowCount={programs.length}
            onRowClick={() => {}}
            onPaginationModelChange={() => {}}
          />
        </Box>
      )}

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              component="span"
              sx={{ fontWeight: 600, fontSize: "20px", color: "#192F67" }}
            >
              {formMode === "create" ? "Create Program" : "Edit Program"}
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              disabled={isModalLoading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {modalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {modalError}
            </Alert>
          )}
          {notificationMessage?.message && (
            <Alert severity={notificationMessage?.type as any} sx={{ mb: 2 }}>
              {notificationMessage?.message}
            </Alert>
          )}
          {!isErrorOnlyMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Point Action</InputLabel>
                <Select
                  value={formValues.PointActionId}
                  onChange={(e) => {
                    const selectedAction = e.target.value as number;
                    const action = pointActionOptions.find(
                      (opt) => opt.value === selectedAction
                    );
                    const isMultiCountSelection = selectedAction === 5;

                    if (
                      isMultiCountSelection &&
                      formMode === "create" &&
                      availableActiveSlots < 2
                    ) {
                      setModalError(
                        "Cannot create Multi Count program. Maximum 3 active programs allowed. Multi Count creates 2-3 programs."
                      );
                      return;
                    }

                    setModalError(null);

                    const canAddThirdMultiCountItem =
                      isMultiCountSelection &&
                      formMode === "create" &&
                      availableActiveSlots >= 3;

                    setFormValues({
                      ...formValues,
                      PointActionId: selectedAction,
                      Item: action?.itemType === "fixed" ? "Visit" : "",
                      Item2: isMultiCountSelection
                        ? formValues.Item2 || ""
                        : "",
                      Item3:
                        isMultiCountSelection && canAddThirdMultiCountItem
                          ? formValues.Item3 || ""
                          : ""
                    });

                    if (!isMultiCountSelection || !canAddThirdMultiCountItem) {
                      setShowThirdItem(false);
                    }
                  }}
                  label="Point Action"
                  disabled={isModalLoading}
                >
                  {pointActionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Points"
                type="number"
                value={formValues.Points}
                onChange={(e) =>
                  setFormValues((v) => ({ ...v, Points: e.target.value }))
                }
                onBlur={() =>
                  setTouchedFields((prev) => ({ ...prev, Points: true }))
                }
                inputProps={{ min: 0, step: 1 }}
                sx={{ mb: 2 }}
                disabled={isModalLoading}
                helperText="Points must be greater than 0"
                error={
                  shouldShowError("Points") && Number(formValues.Points) <= 0
                }
              />
              {(() => {
                const selectedAction = pointActionOptions.find(
                  (opt) => opt.value === formValues.PointActionId
                );
                const isMultiCountSelected = formValues.PointActionId === 5;
                const isDollars = formValues.PointActionId === 4;

                if (selectedAction?.itemType === "fixed") {
                  return (
                    <TextField
                      fullWidth
                      label="Item"
                      value={formValues.Item}
                      disabled
                      helperText="Fixed value for Visit action"
                      sx={{ mb: 2 }}
                    />
                  );
                }

                if (isMultiCountSelected) {
                  if (formMode === "edit") {
                    return (
                      <TextField
                        fullWidth
                        label="Item"
                        value={formValues.Item}
                        onChange={(e) =>
                          setFormValues((v) => ({ ...v, Item: e.target.value }))
                        }
                        onBlur={() =>
                          setTouchedFields((prev) => ({ ...prev, Item: true }))
                        }
                        sx={{ mb: 2 }}
                        disabled={isModalLoading}
                        required
                        error={
                          shouldShowError("Item") &&
                          (!formValues.Item || formValues.Item.trim() === "")
                        }
                        helperText={
                          shouldShowError("Item") &&
                          (!formValues.Item || formValues.Item.trim() === "")
                            ? "Required"
                            : ""
                        }
                      />
                    );
                  }

                  const canAddThirdMultiCountItem = availableActiveSlots >= 3;

                  return (
                    <>
                      <TextField
                        fullWidth
                        label="Item 1"
                        value={formValues.Item}
                        onChange={(e) =>
                          setFormValues((v) => ({ ...v, Item: e.target.value }))
                        }
                        onBlur={() =>
                          setTouchedFields((prev) => ({ ...prev, Item: true }))
                        }
                        sx={{ mb: 2 }}
                        disabled={isModalLoading}
                        required
                        error={
                          shouldShowError("Item") &&
                          (!formValues.Item || formValues.Item.trim() === "")
                        }
                        helperText={
                          shouldShowError("Item") &&
                          (!formValues.Item || formValues.Item.trim() === "")
                            ? "Required"
                            : ""
                        }
                      />
                      <TextField
                        fullWidth
                        label="Item 2"
                        value={formValues.Item2 || ""}
                        onChange={(e) =>
                          setFormValues((v) => ({
                            ...v,
                            Item2: e.target.value
                          }))
                        }
                        onBlur={() =>
                          setTouchedFields((prev) => ({ ...prev, Item2: true }))
                        }
                        sx={{ mb: 2 }}
                        disabled={isModalLoading}
                        required
                        error={
                          shouldShowError("Item2") &&
                          (!formValues.Item2 || formValues.Item2.trim() === "")
                        }
                        helperText={
                          shouldShowError("Item2") &&
                          (!formValues.Item2 || formValues.Item2.trim() === "")
                            ? "Required"
                            : ""
                        }
                      />
                      {canAddThirdMultiCountItem && showThirdItem && (
                        <TextField
                          fullWidth
                          label="Item 3 (Optional)"
                          value={formValues.Item3 || ""}
                          onChange={(e) =>
                            setFormValues((v) => ({
                              ...v,
                              Item3: e.target.value
                            }))
                          }
                          onBlur={() =>
                            setTouchedFields((prev) => ({
                              ...prev,
                              Item3: true
                            }))
                          }
                          sx={{ mb: 2 }}
                          disabled={isModalLoading}
                          helperText="Optional third item"
                        />
                      )}
                      {canAddThirdMultiCountItem && !showThirdItem && (
                        <Box
                          sx={{ mb: 2, display: "flex", alignItems: "center" }}
                        >
                          <IconButton
                            onClick={() => setShowThirdItem(true)}
                            disabled={isModalLoading}
                            sx={{
                              color: "#192F67",
                              border: "1px solid #192F67",
                              "&:hover": {
                                backgroundColor: "#192F67",
                                color: "white"
                              }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                          <Box
                            component="span"
                            sx={{ ml: 1, fontSize: "14px", color: "#666" }}
                          >
                            Add optional third item
                          </Box>
                        </Box>
                      )}
                    </>
                  );
                }

                return (
                  <TextField
                    fullWidth
                    label="Item"
                    value={formValues.Item}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (isDollars) {
                        value = value.replace(/\D/g, "");
                      }
                      setFormValues((v) => ({ ...v, Item: value }));
                    }}
                    onBlur={() =>
                      setTouchedFields((prev) => ({ ...prev, Item: true }))
                    }
                    sx={{ mb: 2 }}
                    disabled={isModalLoading}
                    placeholder={isDollars ? "Enter $ amount" : "Enter text"}
                    helperText={
                      isDollars
                        ? "Enter dollar amount (e.g., 10)"
                        : "Enter text value"
                    }
                    error={
                      shouldShowError("Item") &&
                      (formValues.Item.trim() === "" ||
                        (isDollars &&
                          !!formValues.Item &&
                          (isNaN(parseFloat(formValues.Item)) ||
                            parseFloat(formValues.Item) <= 0 ||
                            !Number.isInteger(parseFloat(formValues.Item)))))
                    }
                    InputProps={{
                      startAdornment: isDollars ? (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ) : undefined
                    }}
                  />
                );
              })()}
              {formMode === "edit" && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.IsActive === 1}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // User is trying to activate
                          const currentProgram = programs.find(
                            (p) => p.Id === formValues.Id
                          );

                          if (!currentProgram) {
                            return;
                          }

                          const otherActiveCount = programs.filter(
                            (p) => p.IsActive === 1 && p.Id !== formValues.Id
                          ).length;
                          
                          if (otherActiveCount >= 3) {
                            setModalError(
                              "Maximum 3 active programs allowed. Please deactivate another program first."
                            );
                            return;
                          }

                          // Check if activating a multi-count program
                          if (currentProgram.PointActionId === 5) {
                            // If no other active programs exist, we need to activate at least one related multi-count
                            if (otherActiveCount === 0) {
                              // Get all inactive programs (all types, not just multi-count)
                              const allInactivePrograms = programs.filter(
                                (p) => p.IsActive !== 1 && p.Id !== currentProgram.Id
                              );

                              if (allInactivePrograms.length > 0) {
                                // Show multi-select dialog with all available programs
                                setSelectedProgramsToActivate([]); // Reset selection
                                setConfirmModalError(null); // Reset error
                                setConfirmModal({
                                  open: true,
                                  program: currentProgram,
                                  relatedPrograms: [],
                                  availablePrograms: allInactivePrograms,
                                  message: `You are activating a Multi Count program, but there are no other active programs. Multi Count programs must be active in groups of 2-3. Please select at least one additional program to activate:`,
                                  action: "activate"
                                });
                                return;
                              } else {
                                // No inactive programs found - show error
                                setModalError(
                                  "Cannot activate a single Multi Count program. Multi Count programs must be active in groups of 2-3. Please create another program first."
                                );
                                return;
                              }
                            }
                          }

                          // Allow activation for non-multi-count or if other programs are already active
                          setModalError(null);
                          setFormValues({
                            ...formValues,
                            IsActive: 1
                          });
                        } else {
                          // User is trying to deactivate
                          const currentProgram = programs.find(
                            (p) => p.Id === formValues.Id
                          );

                          if (!currentProgram) {
                            return;
                          }

                          // Count all active programs
                          const allActiveMultiCountPrograms = programs.filter(
                            (p) => p.PointActionId === 5 && p.IsActive === 1
                          );
                          const activeNonMultiCountPrograms = programs.filter(
                            (p) => p.PointActionId !== 5 && p.IsActive === 1
                          );
                          const totalActivePrograms = allActiveMultiCountPrograms.length + activeNonMultiCountPrograms.length;

                          // Check if it's a multi-count program
                          if (currentProgram.PointActionId === 5) {
                            // Test case 1: Exactly 2 active multi-count programs total AND no other active programs
                            // In this case, we need to deactivate both to prevent crash
                            if (
                              allActiveMultiCountPrograms.length === 2 &&
                              activeNonMultiCountPrograms.length === 0
                            ) {
                              // Find the other active multi-count program
                              const otherActiveMultiCount = allActiveMultiCountPrograms.find(
                                (p) => p.Id !== currentProgram.Id
                              );

                              if (otherActiveMultiCount) {
                                const relatedPrograms = findRelatedMultiCountPrograms(
                                  currentProgram
                                );
                                
                                // Check if the other active multi-count is related (same Points)
                                const isOtherRelated = relatedPrograms.some(
                                  (p) => p.Id === otherActiveMultiCount.Id
                                );

                                // If they're related or if there are exactly 2 active multi-count total
                                // Show confirmation to deactivate both
                                setConfirmModal({
                                  open: true,
                                  program: currentProgram,
                                  relatedPrograms: isOtherRelated 
                                    ? [otherActiveMultiCount] 
                                    : [],
                                  message: `You have exactly 2 active Multi Count programs and no other active programs. Deactivating one will leave only one active Multi Count program, which may cause issues. Do you want to deactivate both Multi Count programs?`,
                                  action: "deactivate"
                                });
                                return;
                              }
                            }

                            // Test case 2: More than 2 active multi-count programs
                            // Just deactivate the one being edited, no confirmation needed
                            if (allActiveMultiCountPrograms.length > 2) {
                              // Allow direct deactivation
                              setModalError(null);
                              setFormValues({
                                ...formValues,
                                IsActive: 0
                              });
                              return;
                            }

                            // Test case 3: 1 active multi-count and other type programs are active
                            // Just deactivate without asking
                            if (
                              allActiveMultiCountPrograms.length === 1 &&
                              activeNonMultiCountPrograms.length > 0
                            ) {
                              // Allow direct deactivation
                              setModalError(null);
                              setFormValues({
                                ...formValues,
                                IsActive: 0
                              });
                              return;
                            }

                            // Test case 4: 1 active multi-count and no other active programs
                            // Allow deactivation (user can deactivate all programs)
                            if (
                              allActiveMultiCountPrograms.length === 1 &&
                              activeNonMultiCountPrograms.length === 0
                            ) {
                              // Allow direct deactivation
                              setModalError(null);
                              setFormValues({
                                ...formValues,
                                IsActive: 0
                              });
                              return;
                            }
                          } else {
                            // For non-multi-count programs: Check if deactivating would leave a single multi-count program
                            // If there are exactly 2 active programs total (1 multi-count + 1 non-multi-count)
                            // and we're deactivating the non-multi-count one, we should also deactivate the multi-count
                            if (
                              totalActivePrograms === 2 &&
                              allActiveMultiCountPrograms.length === 1 &&
                              activeNonMultiCountPrograms.length === 1
                            ) {
                              // Find the active multi-count program
                              const activeMultiCountProgram = allActiveMultiCountPrograms[0];
                              
                              if (activeMultiCountProgram) {
                                // Find related multi-count programs (in case there are multiple in the group)
                                const relatedPrograms = findRelatedMultiCountPrograms(
                                  activeMultiCountProgram
                                );
                                
                                // Get all active related multi-count programs
                                const activeRelatedPrograms = [
                                  activeMultiCountProgram,
                                  ...relatedPrograms.filter(p => p.IsActive === 1)
                                ];

                                setConfirmModal({
                                  open: true,
                                  program: currentProgram,
                                  relatedPrograms: activeRelatedPrograms,
                                  message: `You have exactly 2 active programs (1 Multi Count and 1 other). Deactivating this program would leave only one active Multi Count program, which is not allowed. Do you want to deactivate this program and all related Multi Count programs?`,
                                  action: "deactivate"
                                });
                                return;
                              }
                            }
                          }

                          // For non-multi-count programs or if no special case matched, allow deactivation
                          setModalError(null);
                          setFormValues({
                            ...formValues,
                            IsActive: 0
                          });
                        }
                        setModalError(null);
                        setFormValues({
                          ...formValues,
                          IsActive: e.target.checked ? 1 : 0
                        });
                      }}
                      disabled={isModalLoading || isNonPaying || isBillingLoading}
                      sx={{
                        "&.Mui-checked": {
                          color: "#192F67"
                        }
                      }}
                    />
                  }
                  label="Active"
                  sx={{ mb: 2 }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={handleCloseModal}
            disabled={isModalLoading}
            variant="contained"
            className="blue_button"
          >
            Cancel
          </CustomButton>
          {!isErrorOnlyMode && (
            <CustomButton
              onClick={handleSave}
              disabled={isModalLoading || !isFormValid()}
              variant="contained"
              className="blue_button"
            >
              {isModalLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : formMode === "edit" ? (
                "Update"
              ) : (
                "Save"
              )}
            </CustomButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Multi-Count Activation/Deactivation */}
      <Dialog
        open={confirmModal.open}
        onClose={() => {
          setSelectedProgramsToActivate([]);
          setConfirmModalError(null);
          setConfirmModal({
            open: false,
            program: null,
            relatedPrograms: [],
            availablePrograms: [],
            message: "",
            action: "deactivate"
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              component="span"
              sx={{ fontWeight: 600, fontSize: "20px", color: "#192F67" }}
            >
              {confirmModal.action === "activate"
                ? "Activate Multi Count Program"
                : "Deactivate Multi Count Program"}
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => {
                setSelectedProgramsToActivate([]);
                setConfirmModalError(null);
                setConfirmModal({
                  open: false,
                  program: null,
                  relatedPrograms: [],
                  availablePrograms: [],
                  message: "",
                  action: "deactivate"
                });
              }}
              disabled={isConfirmModalLoading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>{confirmModal.message}</Typography>
          {confirmModalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {confirmModalError}
            </Alert>
          )}
          {confirmModal.action === "activate" &&
            confirmModal.availablePrograms &&
            confirmModal.availablePrograms.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Select programs to activate (maximum 2):
                </Typography>
                <Box
                  sx={{
                    maxHeight: 300,
                    overflowY: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 2
                  }}
                >
                  {confirmModal.availablePrograms.map((program) => {
                    // Check if it's a related multi-count program (same Points value and PointActionId = 5)
                    const isRelated =
                      confirmModal.program &&
                      program.PointActionId === 5 &&
                      confirmModal.program.PointActionId === 5 &&
                      Number(program.Points) === Number(confirmModal.program.Points);
                    const actionLabel = pointActionOptions.find(
                      (opt) => opt.value === program.PointActionId
                    )?.label || "Unknown";
                    
                    return (
                      <Box
                        key={program.Id}
                        sx={{
                          mb: 2,
                          "&:last-child": {
                            mb: 0
                          }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedProgramsToActivate.includes(
                                program.Id
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Maximum 2 programs can be selected (current program + 2 others = 3 total max)
                                  if (selectedProgramsToActivate.length >= 2) {
                                    setConfirmModalError(
                                      "Maximum 3 active programs allowed. You can only select 2 additional programs."
                                    );
                                    return;
                                  }
                                  setConfirmModalError(null); // Clear any previous errors
                                  setSelectedProgramsToActivate([
                                    ...selectedProgramsToActivate,
                                    program.Id
                                  ]);
                                } else {
                                  setConfirmModalError(null); // Clear any previous errors
                                  setSelectedProgramsToActivate(
                                    selectedProgramsToActivate.filter(
                                      (id) => id !== program.Id
                                    )
                                  );
                                }
                              }}
                              disabled={
                                !selectedProgramsToActivate.includes(program.Id) &&
                                selectedProgramsToActivate.length >= 2
                              }
                              sx={{
                                "&.Mui-checked": {
                                  color: "#192F67"
                                },
                                mr: 2
                              }}
                            />
                          }
                          label={
                            <Box sx={{ flex: 1, pt: 0.5 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 500,
                                  mb: isRelated ? 0.5 : 0,
                                  lineHeight: 1.5
                                }}
                              >
                                <strong>{program.Item}</strong> - {actionLabel} (
                                {program.Points} points)
                              </Typography>
                              {isRelated && (
                                <Chip
                                  label="Related Multi Count"
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: "0.7rem",
                                    backgroundColor: "#e3f2fd",
                                    color: "#1976d2",
                                    fontWeight: 500
                                  }}
                                />
                              )}
                            </Box>
                          }
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            m: 0,
                            p: 1.5,
                            borderRadius: 1,
                            border: "1px solid #e0e0e0",
                            backgroundColor: selectedProgramsToActivate.includes(program.Id)
                              ? "#f0f7ff"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: selectedProgramsToActivate.includes(program.Id)
                                ? "#e3f2fd"
                                : "#f9f9f9",
                              borderColor: "#1976d2"
                            },
                            transition: "all 0.2s ease-in-out"
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
                {selectedProgramsToActivate.length === 0 && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Please select at least one program to activate (maximum 2)
                  </Typography>
                )}
                {selectedProgramsToActivate.length === 2 && (
                  <Typography
                    variant="caption"
                    color="info"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Maximum 2 programs selected (3 total active programs limit)
                  </Typography>
                )}
              </Box>
            )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => {
              setSelectedProgramsToActivate([]);
              setConfirmModalError(null);
              setConfirmModal({
                open: false,
                program: null,
                relatedPrograms: [],
                availablePrograms: [],
                message: "",
                action: "deactivate"
              });
            }}
            variant="contained"
            disabled={isConfirmModalLoading}
            className="blue_button"
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={async () => {
              if (!businessId) return;
              if (confirmModal.action === "activate" && isNonPaying) {
                setConfirmModalError(
                  "Access limited: Please renew your contract or fix payment issues to change program status."
                );
                return;
              }
              if (!confirmModal.program || !businessId) return;

              setIsConfirmModalLoading(true);
              try {
                const isActivating = confirmModal.action === "activate";

                if (isActivating) {
                  // Validate that at least one program is selected and max 2
                  if (selectedProgramsToActivate.length === 0) {
                    setConfirmModalError(
                      "Please select at least one program to activate"
                    );
                    setIsConfirmModalLoading(false);
                    return;
                  }
                  if (selectedProgramsToActivate.length > 2) {
                    setConfirmModalError(
                      "Maximum 2 programs can be selected (3 total active programs limit)"
                    );
                    setIsConfirmModalLoading(false);
                    return;
                  }

                  // Get selected programs from availablePrograms
                  const selectedPrograms =
                    confirmModal.availablePrograms?.filter((p) =>
                      selectedProgramsToActivate.includes(p.Id)
                    ) || [];

                  // Include the current program being activated
                  const allProgramsToActivate = [
                    confirmModal.program,
                    ...selectedPrograms
                  ];

                  // Filter to only include inactive programs (IsActive !== 1)
                  const inactiveProgramsToActivate = allProgramsToActivate.filter(
                    (program) => program.IsActive !== 1
                  );

                  if (inactiveProgramsToActivate.length > 0) {
                    await Promise.all(
                      inactiveProgramsToActivate.map((program) =>
                        axios.put(
                          `${process.env.REACT_APP_API_BASE_URL}${UPDATE_PROGRAM_PATH}/${businessId}`,
                          {
                            Id: program.Id,
                            PointActionId: program.PointActionId,
                            Points: program.Points,
                            Item: program.Item,
                            IsActive: 1
                          },
                          { headers }
                        )
                      )
                    );

                    setNotificationMessage({
                      message: `Successfully activated ${inactiveProgramsToActivate.length} program record(s)`,
                      type: "success"
                    });
                  }

                  // Update form values to reflect activation
                  setFormValues({
                    ...formValues,
                    IsActive: 1
                  });
                } else {
                  // Handle deactivation
                  const allActiveMultiCountPrograms = programs.filter(
                    (p) => p.PointActionId === 5 && p.IsActive === 1
                  );
                  const activeNonMultiCountPrograms = programs.filter(
                    (p) => p.PointActionId !== 5 && p.IsActive === 1
                  );

                  let activeProgramsToDeactivate: ProgramItem[] = [];

                  if (
                    allActiveMultiCountPrograms.length === 2 &&
                    activeNonMultiCountPrograms.length === 0
                  ) {
                    // Deactivate ALL active multi-count programs (both of them)
                    activeProgramsToDeactivate = allActiveMultiCountPrograms;
                  } else {
                    // Deactivate the current program and all related multi-count programs
                    const allProgramsToDeactivate = [
                      confirmModal.program,
                      ...confirmModal.relatedPrograms
                    ];
                    
                    // Filter to only include active programs (IsActive === 1)
                    activeProgramsToDeactivate = allProgramsToDeactivate.filter(
                      (program) => program.IsActive === 1
                    );
                  }

                  // Only make API calls if there are active programs to deactivate
                  if (activeProgramsToDeactivate.length > 0) {
                    await Promise.all(
                      activeProgramsToDeactivate.map((program) =>
                        axios.put(
                          `${process.env.REACT_APP_API_BASE_URL}${UPDATE_PROGRAM_PATH}/${businessId}`,
                          {
                            Id: program.Id,
                            PointActionId: program.PointActionId,
                            Points: program.Points,
                            Item: program.Item,
                            IsActive: 0
                          },
                          { headers }
                        )
                      )
                    );

                    const deactivatedCount = activeProgramsToDeactivate.length;
                    const isMultiCountOnly = activeProgramsToDeactivate.every(
                      (p) => p.PointActionId === 5
                    );
                    
                    setNotificationMessage({
                      message: isMultiCountOnly
                        ? `Successfully deactivated ${deactivatedCount} Multi Count program record(s)`
                        : `Successfully deactivated ${deactivatedCount} program record(s)`,
                      type: "success"
                    });
                  }
                }

                // Close confirmation modal first
                setSelectedProgramsToActivate([]);
                setConfirmModalError(null);
                setConfirmModal({
                  open: false,
                  program: null,
                  relatedPrograms: [],
                  availablePrograms: [],
                  message: "",
                  action: "deactivate"
                });

                // Refresh programs list
                await fetchPrograms();

                // Close the edit modal after successful activation or deactivation
                handleCloseModal();
              } catch (error) {
                console.error(
                  `Error ${confirmModal.action === "activate" ? "activating" : "deactivating"} multi-count programs:`,
                  error
                );
                setModalError(
                  `Failed to ${confirmModal.action === "activate" ? "activate" : "deactivate"} programs`
                );
              } finally {
                setIsConfirmModalLoading(false);
              }
            }}
            variant="contained"
            className="blue_button"
            disabled={
              isConfirmModalLoading ||
              (confirmModal.action === "activate" &&
                selectedProgramsToActivate.length === 0)
            }
          >
            {isConfirmModalLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : confirmModal.action === "activate" ? (
              "Activate Selected"
            ) : (
              "Deactivate All"
            )}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </CommonPageLayout>
  );
};

export default ManagePrograms;
